import { useQuery } from "@tanstack/react-query";
import { getEmployeesSingle } from "../api/getEmployeesSingle";
import { TabsWrapper, WrapperAvatar } from "./style";
import { Tab, Tabs } from "shared/components/Tabs";
import { Avatar } from "shared/components/Avatar";
import { StyledLine } from "shared/styles/GlobalStyle";
import { Formik, FormikHelpers } from "formik";
import { WrapperFormRow } from "shared/components/WrapperFormRow";
import { getInitialValues } from "../model/profile/initialValues";
import { GeneralData, NameUserData } from "../model/profile/generalData";
import { DocumentsData } from "../model/profile/documentsData";
import { PlaceData } from "../model/profile/placeData";
import { useEffect, useState } from "react";
import { UploadAvatar } from "shared/containers/UploadAvatar";
import { UploadFile } from "entities/uploadFiles";
import { useFileUploadUp } from "shared/hooks/useFileUploadUp";
import { patchEmployees } from "../api/patchEmployes";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { validateSchemaProfile } from "../model/profile/validateSchema";
import { Loader } from "shared/components/Loader";
import { getRoles } from "features/Setting/api/getRoles";
import { IPropsRoles } from "features/Setting/types";

interface IPropsFile {
    id: number;
    name: string;
    hash_name?: string;
    read_url?: string;
    file?: any;
}

interface IPropsEmployeesProfile {
    isEditable: boolean;
    formikRef: React.Ref<FormikHelpers<any>>;
    setIsEditable: (e: boolean) => void;
    setEditButtonText: (e: string) => void;
    setDisabled: (e: boolean) => void;
}

export const EmployeesProfile = ({
    isEditable,
    formikRef,
    setIsEditable,
    setEditButtonText,
    setDisabled
}:IPropsEmployeesProfile) => {
    const {id} = useParams();

    const [name, setName] = useState('');
    const [newFiles, setNewFiles] = useState<IPropsFile[]>([]);
    const [photo, setPhoto] = useState<File | null>(null);
    const [removedFiles, setRemovedFiles] = useState<IPropsFile[]>([]);

    const [loading, setLoading] = useState(false);
    const [roles, setRoles] = useState<IPropsRoles[]>([]);

    const dispatch = useDispatch();
    const { handleFileUpload } = useFileUploadUp();

    const {isLoading, data, refetch} = useQuery({
        queryKey: ["getEmployeesSingle", id],
        queryFn: () => getEmployeesSingle(id),
        enabled: !!id,
        refetchOnWindowFocus: false
    });

    const {data: dataRoles} = useQuery({
        queryKey: ["getRoles"],
        queryFn: () => getRoles(),
        refetchOnWindowFocus: false
    });

    const [currentPhoto, setCurrentPhoto] = useState<string | null>(data?.profile_photo?.read_url ?? null);


    useEffect(() => {
        if (data && data.user_ad_personal_info && dataRoles) {
            const { surname, first_name, patronymic } = data.user_ad_personal_info;
            const fullName = [surname, first_name, patronymic].filter(Boolean).join(' ');
            setName(fullName);
            setRoles(dataRoles.roles)
        }
    }, [isLoading, data, dataRoles]);

    const onSubmit = async(values: any) => {
        setLoading(true);
        const { uploadedPhotoInfo, uploadedFilesInfo } = await handleFileUpload(photo, newFiles?.map(file => file.file!) || []);
        const updatedValues = {
            // ...getChangedValues(initialValues, values),
            ...values,
            role: Number(values.role),
        };
        if (uploadedPhotoInfo) {
            updatedValues.profile_photo_hash_name = uploadedPhotoInfo.hash_name;
        }
        
        if (removedFiles.length > 0) {
            updatedValues.removed_document_photos_hash_names = removedFiles
                .map((file) => file?.hash_name)
                .filter((hash_name) => hash_name !== null && hash_name !== undefined);
            setRemovedFiles([]);
        }

        if (newFiles.length > 0) {
            updatedValues.new_document_photos_hash_names = uploadedFilesInfo.map((file, index) => ({
                hash_name: file.hash_name,
                file_name: newFiles[index].name
            }))
            setNewFiles([]);
        }

        patchEmployees(
            updatedValues as any,
            refetch,
            setIsEditable,
            setEditButtonText,
            dispatch,
            id
        );
        setTimeout(() => setLoading(false), 1000);
    }    

    const handlePhotoChange = (file: File) => {
        setPhoto(file);
        setCurrentPhoto(URL.createObjectURL(file));
    };

    useEffect(() => {
        if (!currentPhoto) {
            setCurrentPhoto(data?.profile_photo?.read_url ?? null);
        }
    }, [data?.profile_photo?.read_url, currentPhoto]);

    if (isLoading || loading) {
        return <Loader />
    }

    return (
        <>
            {!isLoading && data &&
                <Formik
                    key={id}
                    innerRef={formikRef as any}
                    initialValues={getInitialValues(data)}
                    onSubmit={onSubmit}
                    validationSchema={validateSchemaProfile}
                    enableReinitialize
                >
                    {({ isValid }) => {
                        setDisabled(!isValid);
                        return (
                            <TabsWrapper>
                                <Tabs>
                                    <Tab label="Профиль">
                                        <WrapperAvatar>
                                            {isEditable ? (
                                                <UploadAvatar 
                                                    initialPhoto={currentPhoto}
                                                    onPhotoChange={handlePhotoChange}
                                                />
                                            ) : (
                                                <Avatar photo={data?.profile_photo ? data?.profile_photo?.read_url : ''} name={name}
                                                jobs={data?.role?.name} />
                                            )}
                                        </WrapperAvatar>
                                        <StyledLine margin="20px 0"/>
                                        {isEditable && <WrapperFormRow title="ФИО" formData={NameUserData.map(row => row.map(item => ({ ...item, disabled: !isEditable })))} />}
                                        <WrapperFormRow
                                            title="Общие данные" 
                                            selectValues={roles}
                                            // roleID={data.role.id}
                                            formData={GeneralData.map(row => 
                                                row.map(item => ({ 
                                                    ...item, 
                                                    disabled: item.name === "created_at" ? true : !isEditable,
                                                    placeholder: isEditable ? item.placeholder : "",
                                                    hidden: item.name === "password" && !isEditable ? true : false,
                                                })
                                            ))} />
                                    </Tab>
                                    <Tab label="Документы">
                                        <WrapperFormRow 
                                            title="Документы" 
                                            formData={DocumentsData.map(row => 
                                                row.map(item => ({ 
                                                    ...item, 
                                                    disabled: item.name === "created_at" ? true : !isEditable,
                                                    placeholder: isEditable ? item.placeholder : ""
                                                })
                                            ))} 
                                        />
                                        <div className="container">
                                            <div className="row">
                                                <UploadFile 
                                                    file={data?.user_ad_personal_info.document_photos ? data?.user_ad_personal_info.document_photos as any : []} 
                                                    disabled={isEditable}
                                                    onNewFilesChange={setNewFiles}
                                                    onRemovedFilesChange={setRemovedFiles}
                                                />
                                            </div>
                                        </div>
                                        <WrapperFormRow 
                                            title="Место прописки"
                                            formData={PlaceData.map(row => 
                                                row.map(item => ({ 
                                                    ...item, 
                                                    disabled: item.name === "created_at" ? true : !isEditable,
                                                    placeholder: isEditable ? item.placeholder : ""
                                                })
                                            ))} 
                                        />
                                    </Tab>
                                </Tabs>
                            </TabsWrapper>
                        )
                    }}
                </Formik>
            }
        </>
    )
}